import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { eAccountType, eCampaignStatus, eChannelType, eCreativeStatusBuild, eStandardCampaginChannel, eStandardCampaginChannelName } from '@Workspace/_generated/enums';
import { IBaseCampaignDto, ICreativeDto } from '@Workspace/_generated/interfaces';
import { CampaignController } from '@Workspace/_generated/services';
import { PageLoaderService } from 'libs/services/page-loader/page-loader.service';
import { MessageService } from 'primeng/api';
import * as moment from 'moment';

export interface IHsApiBuildBudgetRow {
    channel: string;
    inventoryType: string;
    flightName: string;
    startDate: Date | string;
    endDate: Date | string;
    budget: number | null;
}

@Component({
    selector: 'hs-api-build-dialog',
    templateUrl: './hs-api-build-dailog.html',
    styleUrls: ['./hs-api-build-dailog.scss']
})
export class HsApiBuildDialog implements OnInit {
    isVisible: boolean = false;
    baseCampaign: IBaseCampaignDto;
    enumChannelType = eChannelType;
    enumStandardCampaginChannel = eStandardCampaginChannel;
    editingIndex: number | null = null;
    editableBudget: number | null = null;
    hasUnsetBudgets: boolean = false;
    buildProcess: string = null;
    private originalBudgets: Map<string, number | null> = new Map();
    displayCreatives: any[] = [];

    constructor(public campaignController: CampaignController,
        public messageService : MessageService,
        private pageLoader: PageLoaderService) {
    }

    ngOnInit(): void {
    }

    openDialog(campaign: IBaseCampaignDto, buildProcess: string){
        const allowedCreativeTypes = [
            eStandardCampaginChannel.Standard_Display_Banner,
            eStandardCampaginChannel.Abandoned_Cart_Display_Banner,
            eStandardCampaginChannel.Post_Purchase_Upsell_Display_Banner,
            eStandardCampaginChannel.Native_Image,
            eStandardCampaginChannel.Native_Video,
            eStandardCampaginChannel.ConnectedTv,
            eStandardCampaginChannel.In_Stream_Video
            // eStandardCampaginChannel.Streaming_Audio
        ];

        this.buildProcess = buildProcess;
        this.baseCampaign = campaign;

        this.displayCreatives = this.baseCampaign.creatives
        .filter(c => allowedCreativeTypes.includes(c.creativeTypeId))
            .sort((a, b) => {

                // Sort by Channel first
                const channelCompare =
                    this.enumChannelType[a.channelId].localeCompare(
                        this.enumChannelType[b.channelId]
                    );

                if (channelCompare !== 0) {
                    return channelCompare;
                }

                // Sort Inventory Type in the same order as allowedCreativeTypes
                return allowedCreativeTypes.indexOf(a.creativeTypeId) -
                    allowedCreativeTypes.indexOf(b.creativeTypeId);
        });

        this.baseCampaign.creatives.forEach(c => {
            this.originalBudgets.set(c.id, c.budget);
        });

        this.hasUnsetBudgets = this.displayCreatives.some(creative => creative.budget === null || creative.budget === 0);
        this.isVisible = true;
    }

    cancel(): void {
        this.baseCampaign.creatives.forEach(c => {
            c.budget = this.originalBudgets?.get(c.id) ?? c.budget;
        });

        this.editingIndex = null;
        this.editableBudget = null;

        this.hasUnsetBudgets = this.displayCreatives.some(
            c => c.budget == null || c.budget === 0
        );

        this.isVisible = false;
    }

    saveAndSend(): void {
        this.isVisible = false;
        this.pageLoader.showPageLoader();

        let isDateDiff: boolean = false;
        if(this.baseCampaign.failedCount > 0 && (this.baseCampaign.campaignStatusId != eCampaignStatus.Campaign_Live && this.baseCampaign.campaignStatusId != eCampaignStatus.Campaign_Paused)){
            let newstartdate = moment(new Date(), 'YYYY-MM-DD 00:00:00').add(2, 'days');
            if(newstartdate.diff(this.baseCampaign.endDate) >= 0){
                this.messageService.add({ severity: 'error', detail: 'Start date is after end date!' });
                isDateDiff = true;
            } 

            else if(newstartdate.diff(this.baseCampaign.startDate) >= 0){
                this.baseCampaign.startDate = newstartdate.utc().startOf('day').toDate();
                if(new Date(this.baseCampaign.creatives.find(x => x.creativeGroup == 1 && x.flightEndDate != null)?.flightEndDate) > this.baseCampaign.startDate)
                    this.baseCampaign.creatives.filter(x => x.creativeGroup == 1).forEach(x => {
                        x.flightStartDate = this.baseCampaign.startDate;
                    });
                else if(new Date(this.baseCampaign.creatives.find(x => x.creativeGroup == 1 && x.flightEndDate != null)?.flightEndDate) <= this.baseCampaign.startDate)
                {
                    this.messageService.add({ severity: 'error', summary: 'Error Message', detail: 'Campaign start date is changed and set to start in next 2 days. Creative flight dates does not correspond to new Campaign start date. Please adjust creative flight dates and try again.', life: 5000 });
                    isDateDiff = true;
                }
            }
        }

        if(!isDateDiff){
            this.baseCampaign.statusId = eCampaignStatus.Sent_to_API_Build;
            this.baseCampaign.dspBuildIndicator = this.buildProcess;

            //Set creative status to Sent to API if creative assets are present
            if(this.baseCampaign.campaignType == eAccountType.FAN365 || this.baseCampaign.campaignType == eAccountType.HS || this.baseCampaign.campaignType == eAccountType.HMP){
                this.baseCampaign.creatives
                    .filter(cr =>
                        cr.creativeTypeId === eStandardCampaginChannel.Standard_Display_Banner ||
                        cr.creativeTypeId === eStandardCampaginChannel.Abandoned_Cart_Display_Banner ||
                        cr.creativeTypeId === eStandardCampaginChannel.Post_Purchase_Upsell_Display_Banner ||
                        cr.creativeTypeId === eStandardCampaginChannel.Premium_Display_Banner ||
                        cr.creativeTypeId === eStandardCampaginChannel.Native_Image ||
                        cr.creativeTypeId === eStandardCampaginChannel.Native_Video ||
                        cr.creativeTypeId === eStandardCampaginChannel.ConnectedTv ||
                        cr.creativeTypeId === eStandardCampaginChannel.In_Stream_Video 
                        // ||
                        // cr.creativeTypeId === eStandardCampaginChannel.Streaming_Audio
                    )
                    .forEach(cr => {
                        if (cr.creativeAssets.some(ca => ca.file) && cr.creativeAssets.filter(ca => ca.file != null).every(ca => ca.isValid === true)) {
                            cr.creativeStatusBuildId = eCreativeStatusBuild.Send_to_API;
                        }
                    });
            }                    

            this.campaignController.SaveCampaign(this.baseCampaign).subscribe(
                async res => {

                    if(res.statusId == this.baseCampaign.statusId){
                        this.messageService.add({ severity: 'success', detail: 'Campaign is successfully sent to API build' });
                    }
                    else{
                        this.messageService.add({ severity: 'error', summary: 'Error Message', detail: 'Campaign was not send to API build. Please try again.', life: 5000 });
                    }
                    
                    // this.confirmationService.close();
                    this.pageLoader.hidePageLoader();
                    if(res.statusId == this.baseCampaign.statusId){
                        await this.BuildAllCampaignsInBackground(this.baseCampaign.id, this.baseCampaign.campaignType, true, this.buildProcess, this.baseCampaign.name,false);
                    }
                },
                err => {
                    this.messageService.add({ severity: 'error', summary: 'Error Message', detail: 'Campaign was not send to API build. Status of campaign is not changed. Please try again.', life: 5000 });
                }
                
            )
        }
    }

    formatDate(dateValue: Date | string): string {
        if (!dateValue) {
            return '';
        }

        if (typeof dateValue === 'string' && dateValue.includes('/')) {
            return dateValue;
        }

        const parsedDate = dateValue instanceof Date ? dateValue : new Date(dateValue);
        if (isNaN(parsedDate.getTime())) {
            return String(dateValue);
        }

        return parsedDate.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        });
    }

    getInventoryName(type: number): string {
         return eStandardCampaginChannelName.get(type)?.name ?? '';
    }

    editBudget(row: any, index: number): void {
        this.editingIndex = index;
        this.editableBudget = row.budget;
    }
    
    saveBudget(row: any): void {
        if (this.editableBudget == null || this.editableBudget === 0) {
            row.budget = null;
        } else {
            row.budget = this.editableBudget;
        }

        this.hasUnsetBudgets = this.displayCreatives.some(
            c => c.budget == null || c.budget === 0
        );

        this.editingIndex = null;
        this.editableBudget = null;
    }

    cancelEdit(): void {
        this.editingIndex = null;
        this.editableBudget = null;
    }

    //Build API Campaign
    async BuildAllCampaignsInBackground(baseCampaignId: string, campaignType: eAccountType, isCampaignBuildTriggeredFromUI: boolean, DSPIndicator: string, campaignName: string,isAutomaticCampaignBuild: boolean) {
        return new Promise<string>((resolve, reject) => {
            this.campaignController.BuildAllPlatformsInBackground(baseCampaignId, campaignType, isCampaignBuildTriggeredFromUI, DSPIndicator, campaignName,isAutomaticCampaignBuild).subscribe();
        });
    }

    isEditDisabled(row: ICreativeDto): boolean {
        return row.creativeStatusBuildId === eCreativeStatusBuild.Send_to_API;
    }
}
